import { AztecAddress } from "@aztec/aztec.js";
import { Button, Card, CardBody, Input, Tab, Tabs } from "@heroui/react";
import { RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { formatUnits } from "viem";
import { useBridgedTokens } from "../../queries/tokenReg";
import { usePrivateBalance, useTokenBalances } from "../../queries/useTokenBalances";
import { safeParseUnits } from "../../utils";
import { SimplePublicBalance } from "../SimpleBalanceDisplay";
import { L2TokenSelector } from "../L2TokenSelector";
import type { ShieldingFormData, ShieldingMode } from "./shieldingConfig";

export function ShieldingForm({
  l2AccountAddress,
  onReview,
}: {
  l2AccountAddress: AztecAddress;
  onReview: (shieldingFormData: ShieldingFormData) => void;
}) {
  const { data: bridgedTokens } = useBridgedTokens();
  const { balances, refetch } = useTokenBalances();

  const [shieldingMode, setShieldingMode] = useState<ShieldingMode>("shield");
  const [selectedTokenIndex, setSelectedTokenIndex] = useState(0);
  const [amount, setAmount] = useState("0.00");

  const selectedToken = bridgedTokens?.[selectedTokenIndex];
  const tokenAddress = selectedToken?.l2Address ?? AztecAddress.ZERO.toString();

  // Get all tokens with balances for the selector
  const tokensWithBalances = useMemo(() => {
    if (!bridgedTokens) return [];

    return bridgedTokens
      .filter((token) => token.l2Address)
      .map((token) => {
        const balance = balances.get(token.l2Address!);
        return balance;
      })
      .filter(
        (data): data is NonNullable<typeof data> =>
          // Show tokens that have loaded and have non-zero public balances
          !!data && data.publicBalance > 0n,
      );
  }, [bridgedTokens, balances]);

  // Get public balance from the unified cache
  const tokenBalance = balances.get(tokenAddress);
  const publicBalance = tokenBalance?.publicBalance;

  // Auto-fetch private balance for the selected token (only when in unshield mode)
  const { privateBalance } = usePrivateBalance(tokenAddress, shieldingMode === "unshield");

  // Handle refresh
  const [isRefetching, setIsRefetching] = useState(false);
  const handleRefresh = async () => {
    setIsRefetching(true);
    try {
      await refetch();
    } finally {
      setIsRefetching(false);
    }
  };

  const publicTokenDecimals = selectedToken?.tokenDecimals;

  useEffect(() => {
    // Reset to first token when tokens load
    if (bridgedTokens && bridgedTokens.length > 0 && !selectedToken) {
      setSelectedTokenIndex(0);
    }
  }, [bridgedTokens, selectedToken]);

  const amountToShield = publicTokenDecimals != null && safeParseUnits(amount.trim(), publicTokenDecimals);
  const isShieldingPossible =
    shieldingMode === "shield"
      ? publicBalance != null &&
        publicBalance >= 0n &&
        !!amountToShield &&
        amountToShield > 0n &&
        amountToShield <= publicBalance
      : privateBalance != null &&
        privateBalance >= 0n &&
        !!amountToShield &&
        amountToShield > 0n &&
        amountToShield <= privateBalance;

  function setMaxAmount() {
    if (publicTokenDecimals != null) {
      if (shieldingMode === "shield") {
        setAmount(formatUnits(publicBalance ?? 0n, publicTokenDecimals));
      } else {
        setAmount(formatUnits(privateBalance ?? 0n, publicTokenDecimals));
      }
    }
  }

  return (
    <Card
      classNames={{
        base: "justify-center turnstile-card-border row-start-2 col-span-4",
        body: "gap-6 p-6",
      }}
    >
      <CardBody>
        <Tabs
          fullWidth
          color="primary"
          variant="bordered"
          selectedKey={shieldingMode}
          onSelectionChange={(key) => setShieldingMode(key as ShieldingMode)}
          classNames={{
            tabList: "bg-[#33546C] border-none p-1.5",
            tab: "text-medium font-medium",
            tabContent: "text-white group-data-[selected=true]:text-white",
            cursor: "bg-[#14DBC0]/20 outline outline-2 outline-[#14DBC0]",
          }}
        >
          <Tab key="shield" title="Shield" />
          <Tab key="unshield" title="Unshield" />
        </Tabs>

        <div className="flex flex-col gap-1 items-stretch">
          <div className="flex divide-x-2 divide-[#203E54] bg-[#33546C] rounded-medium overflow-clip">
            <L2TokenSelector
              l2Tokens={tokensWithBalances}
              tokenAddress={tokenAddress}
              onTokenAddressChange={(newAddress) => {
                // Find the index of the selected token
                const index = bridgedTokens?.findIndex((t) => t.l2Address === newAddress) ?? 0;
                setSelectedTokenIndex(index);
              }}
            />
            <Input
              type="number"
              label="Amount"
              labelPlacement="inside"
              aria-label="Amount to shield"
              placeholder="Amount to shield"
              value={amount}
              onValueChange={(value) => setAmount(value)}
              radius="none"
              size="lg"
              classNames={{
                inputWrapper: "data-[hover=true]:bg-transparent shadow-none",
                input: "placeholder:text-[#ADC0DD]",
              }}
            />
          </div>

          <div className="flex items-center text-small">
            {shieldingMode === "shield" ? (
              <div className="flex justify-between flex-1">
                <span>Public Balance:</span>
                <SimplePublicBalance
                  tokenAddress={tokenAddress}
                  decimals={selectedToken?.tokenDecimals ?? 18}
                />
              </div>
            ) : (
              <div className="flex justify-between flex-1">
                <span>Private Balance:</span>
                <span>
                  {privateBalance !== undefined ? formatUnits(privateBalance, selectedToken?.tokenDecimals ?? 18) : "..."}
                </span>
              </div>
            )}
            <div className="flex gap-1">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                color="primary"
                className="h-auto min-w-max p-1"
                onPress={handleRefresh}
                isDisabled={isRefetching}
                aria-label="Refresh balance"
              >
                <RefreshCw className={`h-4 w-4 ${isRefetching ? "animate-spin" : ""}`} />
              </Button>
              <Button
                variant="light"
                size="sm"
                color="primary"
                className="h-auto min-w-max px-2 py-1 font-semibold"
                fullWidth={false}
                onPress={setMaxAmount}
              >
                MAX
              </Button>
            </div>
          </div>
        </div>

        <Button
          fullWidth
          size="lg"
          color="primary"
          radius="full"
          isDisabled={!isShieldingPossible}
          onPress={() => {
            if (!amountToShield) {
              throw new Error("Amount to shield is not valid");
            }

            if (!selectedToken) {
              throw new Error("No token selected");
            }

            onReview({
              shieldingMode,
              l2AccountAddress,
              tokenAddress: AztecAddress.fromString(tokenAddress),
              amount: amountToShield,
              displayAmount: Number(amount),
              tokenSymbol: selectedToken.tokenSymbol,
              tokenDecimals: Number(publicTokenDecimals),
              publicBalance: publicBalance ?? 0n,
              privateBalance: privateBalance ?? 0n,
            });
          }}
        >
          Review
        </Button>
      </CardBody>
    </Card>
  );
}
